import MobileStickyActionBar from '../Generic/mobile/MobileStickyActionBar';
import React from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Pagination,
    Select,
    Snackbar,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {useTranslation} from 'react-i18next';
import {useContractsList, useContractUpload} from '../../hooks/useContracts';
import ContractStatCards from '../Generic/ContractStatCards';
import ContractsTable from './tables/ContractsTable';
import ContractsTableMobile from './mobile/ContractsTableMobile';
import EditContractDialog from './dialogs/EditContractDialog';
import ViewContractDialog from './dialogs/ViewContractDialog';
import ContractPreviewDialog from './dialogs/ContractPreviewDialog';
import AddContractDialog from './dialogs/AddContractDialog';
import ConfirmDeleteActionDialog from '../Generic/dialogs/ConfirmDeleteActionDialog';
import UploadContractPreviewDialog from '../Generic/dialogs/UploadContractPreviewDialog';

export default function Contracts(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLElement>(null);
    const [moreAnchorEl, setMoreAnchorEl] = React.useState<null | HTMLElement>(null);

    const {
        branches,
        branchesLoading,
        selectedBranch,
        handleBranchChange,
        typeFilter,
        handleTypeFilterChange,
        contractStats,
        searchInput,
        setSearchInput,
        contractsLoading,
        contractsError,
        paginatedContracts,
        page,
        setPage,
        totalPages,
        viewDialogOpen,
        viewingContract,
        handleView,
        handleCloseView,
        editDialogOpen,
        editForm,
        editBranchName,
        editSubmitError,
        editSubmitting,
        handleEdit,
        handleCloseEditDialog,
        handleEditFormChange,
        handleSubmitEdit,
        terminateDialogOpen,
        handleTerminateClick,
        handleCloseTerminateDialog,
        handleConfirmTerminate,
        handleDownloadPdf,
        handleDownloadWord,
        downloadingContractId,
        previewContract,
        previewUrl,
        previewDoc,
        previewLoading,
        signing,
        handlePreviewContract,
        handleClosePreview,
        handleSignContract,
        successMessage,
        errorMessage,
        handleCloseSnackbar,
        handleContractsUploaded,
        employees,
        employeesInAddBranch,
        allAddBranchEmployeesHaveContract,
        addDialogOpen,
        addBranchId,
        addEmployeeId,
        addForm,
        addSubmitError,
        addSubmitting,
        handleOpenAddDialog,
        handleCloseAddDialog,
        handleAddBranchChange,
        handleAddEmployeeChange,
        handleAddFormChange,
        handleSubmitAdd,
    } = useContractsList();

    const {
        uploadingContracts,
        contractUploadPreviewOpen,
        uploadedContracts,
        contractUploadSaveLoading,
        contractUploadSaveError,
        contractSalaryBasisOptions,
        contractSalaryBasisLoading,
        contractCalcTypeOptions,
        contractCalcTypeLoading,
        handleUploadContracts,
        contractUploadError,
        clearContractUploadError,
        downloadContractTemplate,
        contractTemplateDownloading,
        handleUploadedContractChange,
        handleCloseContractUploadPreview,
        handleSaveUploadedContracts,
    } = useContractUpload(employees, handleContractsUploaded);

    return (
        <>
            <Snackbar
                open={!!successMessage}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert severity="success" onClose={handleCloseSnackbar} sx={{width: '100%'}}>
                    {successMessage}
                </Alert>
            </Snackbar>
            <Snackbar
                open={!!errorMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            >
                <Alert severity="error" onClose={handleCloseSnackbar} sx={{width: '100%'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>

            {/* Upload error (row-level errors from POST /api/contract/upload) */}
            <Dialog open={!!contractUploadError} onClose={clearContractUploadError} fullWidth maxWidth="sm">
                <DialogTitle>{t('employee.upload.error.title')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{mb: contractUploadError?.errors.length ? 1.5 : 0}}>
                        {contractUploadError?.message}
                    </Typography>
                    {contractUploadError?.errors.map((e, i) => (
                        <Alert key={i} severity="error" sx={{mb: 1, wordBreak: 'break-word'}}>
                            {e.row == null
                                ? e.message
                                : e.column
                                    ? t('employee.upload.error.rowError', {row: e.row, column: e.column, message: e.message})
                                    : t('employee.upload.error.rowOnly', {row: e.row, message: e.message})}
                        </Alert>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={clearContractUploadError}>{t('employee.upload.error.close')}</Button>
                </DialogActions>
            </Dialog>

            {/* Page Header: Title + Subtitle + Create action */}
            <Box sx={{
                width: '100%',
                mb: 3,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: {xs: 'flex-start', sm: 'center'},
                gap: 2,
            }}>
                <Box sx={{textAlign: 'left'}}>
                    <Typography variant="h5" sx={{fontWeight: 700, textAlign: 'left'}}>
                        {t('contracts.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'left'}}>
                        {t('contracts.subtitle')}
                    </Typography>
                </Box>
                {!isMobile && (
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<AddIcon/>}
                        onClick={handleOpenAddDialog}
                        sx={{borderRadius: 1, px: 3}}
                    >
                        {t('contracts.add.button')}
                    </Button>
                )}
            </Box>

            {/* Summary Stat Cards */}
            <Box sx={{mb: 3}}>
                <ContractStatCards stats={contractStats} loading={contractsLoading}/>
            </Box>

            {isMobile ? (
                <>
                    <Card variant="outlined" sx={{borderRadius: 1, mb: 1.5}}>
                        <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={t('contracts.search.placeholder')}
                                variant="outlined"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action"/>
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchInput ? (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setSearchInput('')}>
                                                <ClearIcon fontSize="small"/>
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{borderRadius: 1, mb: 2}}>
                        <CardContent sx={{
                            p: 1.5,
                            '&:last-child': {pb: 1.5},
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<FilterListIcon/>}
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                            >
                                {t('employee.filters.button')}
                            </Button>
                            <Menu
                                anchorEl={filterAnchorEl}
                                open={Boolean(filterAnchorEl)}
                                onClose={() => setFilterAnchorEl(null)}
                            >
                                <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220}}>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="contracts-branch-filter-mobile-label">{t('contracts.table.header.branch')}</InputLabel>
                                        <Select
                                            labelId="contracts-branch-filter-mobile-label"
                                            label={t('contracts.table.header.branch')}
                                            value={selectedBranch}
                                            onChange={handleBranchChange}
                                            disabled={branchesLoading}
                                        >
                                            <MenuItem value="All">{t('common.filters.all')}</MenuItem>
                                            {branches.map((branch) => (
                                                <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="contracts-type-filter-mobile-label">{t('contracts.filters.type')}</InputLabel>
                                        <Select
                                            labelId="contracts-type-filter-mobile-label"
                                            label={t('contracts.filters.type')}
                                            value={typeFilter}
                                            onChange={handleTypeFilterChange}
                                        >
                                            <MenuItem value="">{t('common.filters.all')}</MenuItem>
                                            <MenuItem value="open-ended">{t('contracts.filters.type.openEnded')}</MenuItem>
                                            <MenuItem value="fixed-term">{t('contracts.filters.type.fixedTerm')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Menu>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {t('contracts.count', {count: paginatedContracts.length})}
                            </Typography>

                            <IconButton size="small" onClick={(e) => setMoreAnchorEl(e.currentTarget)} sx={{flexShrink: 0}}>
                                <MoreVertIcon/>
                            </IconButton>
                            <Menu
                                anchorEl={moreAnchorEl}
                                open={Boolean(moreAnchorEl)}
                                onClose={() => setMoreAnchorEl(null)}
                            >
                                <MenuItem
                                    onClick={() => {
                                        downloadContractTemplate();
                                        setMoreAnchorEl(null);
                                    }}
                                    disabled={contractTemplateDownloading}
                                >
                                    {contractTemplateDownloading ? (
                                        <CircularProgress size={16} sx={{mr: 1.5}}/>
                                    ) : (
                                        <DescriptionOutlinedIcon fontSize="small" sx={{mr: 1.5}}/>
                                    )}
                                    {t('employee.table.downloadTemplate')}
                                </MenuItem>
                                <MenuItem component="label" disabled={uploadingContracts}>
                                    {uploadingContracts ? (
                                        <CircularProgress size={16} sx={{mr: 1.5}}/>
                                    ) : (
                                        <UploadFileIcon fontSize="small" sx={{mr: 1.5}}/>
                                    )}
                                    {uploadingContracts ? t('common.uploading') : t('contracts.upload.button')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".xlsx,.xls,.csv"
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleUploadContracts(file);
                                            e.target.value = '';
                                            setMoreAnchorEl(null);
                                        }}
                                    />
                                </MenuItem>
                            </Menu>
                        </CardContent>
                    </Card>

                    <ContractsTableMobile
                        contracts={paginatedContracts}
                                            onPreview={handlePreviewContract}
                        loading={contractsLoading}
                        error={contractsError}
                        hasBranchSelected={!!selectedBranch}
                        searchQuery={searchInput}
                        onEdit={handleEdit}
                        onView={handleView}
                        onTerminate={handleTerminateClick}
                        onDownloadPdf={handleDownloadPdf}
                        onDownloadWord={handleDownloadWord}
                        downloadingContractId={downloadingContractId}
                    />

                    {totalPages > 1 && (
                        <Box sx={{display: 'flex', justifyContent: 'center', mt: 3}}>
                            <Pagination
                                color="primary"
                                count={totalPages}
                                page={page}
                                onChange={(_event, value) => setPage(value)}
                            />
                        </Box>
                    )}

                    <MobileStickyActionBar>
                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            startIcon={<AddIcon/>}
                            onClick={handleOpenAddDialog}
                        >
                            {t('contracts.add.button')}
                        </Button>
                    </MobileStickyActionBar>
                </>
            ) : (
                <Card variant="outlined" sx={{borderRadius: 1}}>
                    <CardContent>
                        <Typography variant="h6" sx={{fontWeight: 700, mb: 2, textAlign: 'left'}}>
                            {t('contracts.list.title')}
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            overflowX: 'auto',
                            overflowY: 'visible',
                            pb: 1,
                            pt: 0.5,
                        }}>
                            <Box sx={{flex: '1 1 260px', minWidth: 220}}>
                                <TextField
                                    fullWidth
                                    placeholder={t('contracts.search.placeholder')}
                                    variant="outlined"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action"/>
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchInput ? (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setSearchInput('')}>
                                                    <ClearIcon fontSize="small"/>
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    }}
                                />
                            </Box>

                            <FormControl size="small" sx={{minWidth: 180, flexShrink: 0}}>
                                <InputLabel id="contracts-branch-select-label">{t('contracts.table.header.branch')}</InputLabel>
                                <Select
                                    labelId="contracts-branch-select-label"
                                    id="contracts-branch-select"
                                    value={selectedBranch}
                                    label={t('contracts.table.header.branch')}
                                    onChange={handleBranchChange}
                                    disabled={branchesLoading}
                                >
                                    <MenuItem value="All">{t('common.filters.all')}</MenuItem>
                                    {branches.map((branch) => (
                                        <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{minWidth: 160, flexShrink: 0}}>
                                <InputLabel id="contracts-type-select-label">{t('contracts.filters.type')}</InputLabel>
                                <Select
                                    labelId="contracts-type-select-label"
                                    id="contracts-type-select"
                                    value={typeFilter}
                                    label={t('contracts.filters.type')}
                                    onChange={handleTypeFilterChange}
                                >
                                    <MenuItem value="">{t('common.filters.all')}</MenuItem>
                                    <MenuItem value="open-ended">{t('contracts.filters.type.openEnded')}</MenuItem>
                                    <MenuItem value="fixed-term">{t('contracts.filters.type.fixedTerm')}</MenuItem>
                                </Select>
                            </FormControl>

                            <Button
                                variant="outlined"
                                size="small"
                                onClick={downloadContractTemplate}
                                disabled={contractTemplateDownloading}
                                startIcon={contractTemplateDownloading ? <CircularProgress size={16} color="inherit"/> : <DescriptionOutlinedIcon/>}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {t('employee.table.downloadTemplate')}
                            </Button>

                            <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                disabled={uploadingContracts}
                                startIcon={uploadingContracts ? <CircularProgress size={16} color="inherit"/> : <UploadFileIcon/>}
                                sx={{flexShrink: 0, whiteSpace: 'nowrap'}}
                            >
                                {uploadingContracts ? t('common.uploading') : t('contracts.upload.button')}
                                <input
                                    type="file"
                                    hidden
                                    accept=".xlsx,.xls,.csv"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleUploadContracts(file);
                                        e.target.value = '';
                                    }}
                                />
                            </Button>
                        </Box>

                        <Box sx={{mb: 1}}>
                            <Typography variant="body2" color="text.secondary">
                                {t('contracts.count', {count: paginatedContracts.length})}
                            </Typography>
                        </Box>

                        <ContractsTable
                            contracts={paginatedContracts}
                                                    onPreview={handlePreviewContract}
                            loading={contractsLoading}
                            error={contractsError}
                            hasBranchSelected={!!selectedBranch}
                            searchQuery={searchInput}
                            onEdit={handleEdit}
                            onView={handleView}
                            onTerminate={handleTerminateClick}
                            onDownloadPdf={handleDownloadPdf}
                            onDownloadWord={handleDownloadWord}
                            downloadingContractId={downloadingContractId}
                        />

                        {totalPages > 1 && (
                            <Box sx={{display: 'flex', justifyContent: 'center', mt: 3}}>
                                <Pagination
                                    color="primary"
                                    count={totalPages}
                                    page={page}
                                    onChange={(_event, value) => setPage(value)}
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            <EditContractDialog
                open={editDialogOpen}
                contract={editForm}
                branchName={editBranchName}
                submitting={editSubmitting}
                onClose={handleCloseEditDialog}
                onSubmit={handleSubmitEdit}
                handleContractFormChange={handleEditFormChange}
                contractSubmitError={editSubmitError}
            />

            <ContractPreviewDialog
                open={previewContract !== null}
                url={previewUrl}
                loading={previewLoading}
                signed={previewDoc?.signed ?? false}
                signing={signing}
                onClose={handleClosePreview}
                onSign={handleSignContract}
                onDownload={() => previewContract && handleDownloadPdf(previewContract)}
            />
            <ViewContractDialog
                open={viewDialogOpen}
                contract={viewingContract}
                onClose={handleCloseView}
            />

            <ConfirmDeleteActionDialog
                open={terminateDialogOpen}
                message={t('employee.contract.confirmTerminationMessage')}
                onClose={handleCloseTerminateDialog}
                onNo={handleCloseTerminateDialog}
                onYes={handleConfirmTerminate}
            />

            <AddContractDialog
                open={addDialogOpen}
                branches={branches}
                branchId={addBranchId}
                employees={employeesInAddBranch}
                allEmployeesHaveContract={allAddBranchEmployeesHaveContract}
                employeeId={addEmployeeId}
                contract={addForm}
                submitting={addSubmitting}
                onClose={handleCloseAddDialog}
                onSubmit={handleSubmitAdd}
                onBranchChange={handleAddBranchChange}
                onEmployeeChange={handleAddEmployeeChange}
                handleContractFormChange={handleAddFormChange}
                contractSubmitError={addSubmitError}
            />

            <UploadContractPreviewDialog
                open={contractUploadPreviewOpen}
                contracts={uploadedContracts}
                employees={employees}
                salaryBasisOptions={contractSalaryBasisOptions}
                salaryBasisLoading={contractSalaryBasisLoading}
                calcTypeOptions={contractCalcTypeOptions}
                calcTypeLoading={contractCalcTypeLoading}
                onClose={handleCloseContractUploadPreview}
                onSave={handleSaveUploadedContracts}
                onChange={handleUploadedContractChange}
                saveLoading={contractUploadSaveLoading}
                saveError={contractUploadSaveError}
            />
        </>
    );
}
